import { type ReactNode, useContext, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { firstHeldPermission } from '../ui/permissions/firstHeldPermission';
import { PermissionContext, usePermission } from '../ui/permissions/permissionContext';
import { NoAccessState } from './NoAccessState';
import { type NavId, navConfig } from './navConfig';
import { focusPageHeading } from './PageHeadingFocus';

/** The section the route belongs to: a navigation entry, or one page of Settings with its own key. */
type RouteSection =
    | {
          /** The navigation entry whose keys guard this route (any one held); `email/mailbox` passes `email`. */
          readonly nav: NavId;
          readonly permission?: never;
      }
    | {
          /**
           * The key a page inside Settings needs — each page has its own (AD #41): the tags pages, the email
           * templates, Users and Roles (design-system §8, Rev 70; `settingsPages.ts`).
           */
          readonly permission: string;
          readonly nav?: never;
      };

export type RoutePermissionGuardProps = RouteSection & {
    /**
     * A key the route also needs **when it is entered**, on top of the entry's — `account.manage` on the
     * account form routes (F1-b WI-1.2 plan Decision 2). Checked once per location key: losing it while
     * the route is open does not swap the page, so a form can say why its save was refused.
     */
    readonly entryPermission?: string;
    readonly children: ReactNode;
};

/**
 * Wraps a section route so a deep link is gated exactly like its sidebar entry (F5-k FR-17): it
 * looks the entry's keys up in `navConfig`, takes the first held one (`firstHeldPermission`, as the
 * sidebar does — any one suffices, F3-a FR-V7) and asks `usePermission`, the resolver
 * `PermissionGuard` reads, so the route and the sidebar cannot disagree. A held key renders the section; anything
 * else renders the no-access state in place (F5-k FR-18). An id with no entry fails closed.
 *
 * Sits outside the lazily loaded section element, so a user without the key never downloads the
 * section's chunk.
 *
 * **The entry key** (`entryPermission`, F1-b WI-1.2 plan Decision 2). A route that also needs a key
 * to be opened — a create or edit form behind its section's view key — names it here. It is checked
 * when a location is entered: without it the route renders the no-access state, and the chunk is never
 * downloaded. While the route stays at that location the entry check is not re-run, so a form whose
 * save is refused `403` stays on screen to say why (F1-b spec AC-13b), instead of being swapped out
 * mid-save; the navigation entry's key is still checked on every render, so losing it swaps the page
 * at once. Without the prop the guard behaves exactly as before.
 *
 * **Focus (F5-k WI-2.2 plan Decision 8).** When a `/me` re-read turns a section that was on screen into the no-access
 * state, the focused control inside it is unmounted and focus falls to `<body>`, so a screen-reader
 * user hears nothing. In that case — and only when focus is inside `<main>` or on `<body>` — focus
 * moves to the state's heading. A first render that is already the state is left to
 * `PageHeadingFocus`, and focus anywhere else (the sidebar, another control) is never moved.
 *
 * React Router renders each matched element under an unkeyed route wrapper, so one instance of this
 * component is reused across the sibling section routes. The record of what was on screen is
 * therefore tied to the location: a render at a new location key (any navigation: push, replace,
 * Back/Forward, a search-param rewrite) starts a fresh record and never moves focus.
 */
export function RoutePermissionGuard({
    nav,
    permission,
    entryPermission,
    children,
}: RoutePermissionGuardProps) {
    const entry = nav === undefined ? undefined : navConfig.find((item) => item.id === nav);
    // A Settings page asks about the key it names; a section, about its entry's first held key (any one
    // suffices — F3-a FR-V7), the one the sidebar's guard is handed too. Called unconditionally (hooks
    // rule); the missing-entry case is refused below.
    const resolve = useContext(PermissionContext);
    const decision = usePermission(
        entry === undefined ? (permission ?? '') : firstHeldPermission(resolve, entry.permissionKeys),
    );
    const entryDecision = usePermission(entryPermission ?? '');
    const entryHeldNow = entryPermission === undefined || entryDecision.kind === 'allowed';

    const { key } = useLocation();
    // The entry check as of the render that entered this location with this entry key — adjusted
    // during render when either changes, React's pattern for state that follows a prop.
    const [entered, setEntered] = useState({ key, entryPermission, held: entryHeldNow });
    let enteredHeld = entered.held;
    if (entered.key !== key || entered.entryPermission !== entryPermission) {
        enteredHeld = entryHeldNow;
        setEntered({ key, entryPermission, held: entryHeldNow });
    }

    // A `nav` id with no entry fails closed, as does an empty Settings key.
    const known = entry !== undefined || (permission !== undefined && permission !== '');
    const allowed = known && decision.kind === 'allowed' && enteredHeld;

    const stateRef = useRef<HTMLDivElement>(null);
    const lastRendered = useRef({ allowed, key });

    useLayoutEffect(() => {
        const previous = lastRendered.current;
        lastRendered.current = { allowed, key };
        // A new location is a first render of whichever section it shows, not a loss of access.
        if (previous.key !== key || !previous.allowed || allowed) {
            return;
        }
        const main = stateRef.current?.closest('main');
        if (main === null || main === undefined) {
            return;
        }
        const active = document.activeElement;
        if (active === null || active === document.body || main.contains(active)) {
            focusPageHeading(main);
        }
    }, [allowed, key]);

    return allowed ? children : <NoAccessState ref={stateRef} />;
}
